import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { availableSearchSources, SearchResponseStatus, SearchType } from 'fym-common/src/types/searchService';
import { InspectType } from 'fym-common/src/types/inspectService';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { useLazyInspectByUrlQuery, useLazySearchQuery } from '../../../../redux/api/musicAPI';
import { ReleaseLinkData } from '../releaseLink/types';
import {
  parseInspectResultToReleaseLinkData,
  parseSearchResults,
  parseUrlToReleaseLinkData,
} from '../releaseLink/utils';
import { ReleaseResolution } from './releaseResolutionTypes';

/** The host and path of a url, for the line under the title. */
function shortenUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '');
}

/**
 * A pasted url turned into the links a release page needs.
 *
 * Two calls, in order: `inspect/url` says what the link points at, then one
 * search fans out to every other music service. It is the flow the Release Link
 * wizard already ran; the sheet reports it a step at a time instead of holding
 * the artist behind it.
 */
export function usePastedUrlResolution(url: string | undefined): ReleaseResolution {
  const [inspectByUrl] = useLazyInspectByUrlQuery();
  const [search] = useLazySearchQuery();

  const [main, setMain] = useState<ReleaseLinkData>();
  // `undefined` while the search is still out, an array once it has answered.
  const [searched, setSearched] = useState<ReleaseLinkData[]>();
  const [hasFailed, setHasFailed] = useState(false);
  const startedForUrl = useRef<string | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (url === undefined || startedForUrl.current === url) {
      return;
    }
    startedForUrl.current = url;
    setMain(undefined);
    setSearched(undefined);
    setHasFailed(false);

    inspectByUrl(url)
      .then((inspectResult) => {
        if (!inspectResult.isSuccess) {
          setHasFailed(true);
          setSearched([]);
          return;
        }
        let resolved: ReleaseLinkData | undefined;
        if (inspectResult.data.type === InspectType.unknown) {
          // A link we cannot read is still a link. The page gets one button.
          resolved = parseUrlToReleaseLinkData(url);
        } else if (inspectResult.data.status === SearchResponseStatus.success) {
          resolved = parseInspectResultToReleaseLinkData(inspectResult.data);
        }
        if (!resolved) {
          setHasFailed(true);
          setSearched([]);
          return;
        }
        setMain(resolved);

        const { type, source, data } = resolved;
        if (type === SearchType.unknown) {
          // Nothing was recognised, so there is nothing to search other
          // services by. The single pasted link is the whole answer.
          setSearched([]);
          return;
        }
        search({
          track: data.track ?? undefined,
          album: data.album ?? undefined,
          artist: data.artist ?? undefined,
          sources: availableSearchSources.filter((importer) => importer !== source),
          type,
        })
          .then((searchResult) => {
            if (!searchResult.isSuccess) {
              setSearched([]);
              return;
            }
            const { tracks, albums } = searchResult.data;
            setSearched(parseSearchResults([...(tracks ?? []), ...(albums ?? [])]));
          })
          .catch(() => setSearched([]));
      })
      .catch(() => {
        setHasFailed(true);
        setSearched([]);
      });
  }, [attempt, inspectByUrl, search, url]);

  const retry = useCallback(() => {
    startedForUrl.current = undefined;
    setAttempt((value) => value + 1);
  }, []);

  return useMemo(() => {
    const links = main ? [main, ...(searched ?? [])] : [];
    const [, ...others] = links;
    const foundSources = links.reduce<MusicAPIIntegrationID[]>((sources, link) => {
      if (link.source !== undefined && !sources.includes(link.source)) {
        sources.push(link.source);
      }
      return sources;
    }, []);
    const searchedSources = availableSearchSources.filter((source) => source !== main?.source);
    const isSearching = url !== undefined && searched === undefined;

    return {
      isResolving: isSearching,
      retry,
      main,
      others,
      foundSources,
      pendingSources: isSearching ? searchedSources : [],
      missingSources: isSearching ? [] : searchedSources.filter((source) => !foundSources.includes(source)),
      title: main?.data.track ?? main?.data.album ?? undefined,
      artistName: main?.data.artist ?? undefined,
      albumName: main?.data.album ?? undefined,
      imageUrl: main?.data.imageUrl ?? undefined,
      isrc: main?.data.isrc ?? undefined,
      sourceLabel: url === undefined ? undefined : shortenUrl(url),
      hasNoLinks: hasFailed && !isSearching,
    };
  }, [hasFailed, main, retry, searched, url]);
}
